import Cookies from "js-cookie";
import {routes, usePostData} from "../routes/api";
import {getCreateFields, getRequiredFields, isDict} from "../util/util";
import React, {useEffect, useLayoutEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {LoadingComponent} from "../util/loading";
import {Grid, Typography} from "@mui/material";
import ErrorDisplay from "../components/errorDisplay";
import NavigateBreadcrumbs from "../components/navBreadcrumbs";
import InputField from "../components/inputField";
import RelationView from "../components/relationView";
import Button from "@mui/material/Button";
import {organizationFields as orgFields} from "../util/modelFields";


export default function OrganizationCreate({}) {
    const token = Cookies.get('token');
    const orgRoute = routes.organization;
    const permissions = Cookies.get('permissions') ?
        JSON.parse(Cookies.get('permissions')).organization :
        {create: false, edit: false, delete: false, view: false};
    const webAdmin = Cookies.get('webAdmin') === 'T';
    const requiredFields = getRequiredFields(orgFields.fields);
    const fields = getCreateFields(orgFields.fields);
    const [orgFormdata, setOrgFormdata] = useState(Object.values(orgFields.fields).filter(item => item.defaultValue).reduce((acc, item) => ({
        ...acc,
        [item.alias]: item.defaultValue
    }), {}));
    const [touched, setTouched] = useState({});
    const [error, setError] = useState(false);
    const [fieldChecks, setFieldChecks] = useState({});
    const [alert, setAlert] = useState(false);
    const [errorFields, setErrorFields] = useState([]);
    const [loading, setLoading] = useState(false);

    const {
        data: orgPostResp,
        statusCode: orgPostStatus,
        handleCall: postOrg
    } = usePostData(token, orgRoute);

    const navigate = useNavigate();
    const location = useLocation();

    const from = location.state?.from ? location.state.from : {pathname: "/"};

    useEffect(() => {
        const regexError = Object.values(fieldChecks).reduce((acc, item, _) => (acc || item), false);
        setError(requiredFields.reduce((acc, item, _) => (acc || (!orgFormdata[item])), false) || regexError);
    }, [requiredFields, orgFormdata])

    const handleOrgChange = (e, regexp) => {
        if (!e.target.value) {
            const {[e.target.name]: _, ...formData} = orgFormdata;
            setOrgFormdata(formData);
        } else {
            setOrgFormdata({
                ...orgFormdata,
                [e.target.name]: e.target.value
            });
            if (regexp) {
                setFieldChecks({
                    ...fieldChecks,
                    [e.target.name]: !regexp.test(e.target.value)
                });
            }
        }

        setErrorFields(errorFields.filter(item => item !== e.target.name));
    }

    const handleDateChange = (key, value) => {
        setOrgFormdata({
            ...orgFormdata,
            [key]: value.format('YYYY-MM-DD')
        })
    }

    const handleEnumChange = (e) => {
        setOrgFormdata({
            ...orgFormdata,
            [e.target.name]: e.target.value
        })
    }

    const handleRelationChange = (newValue, key, multiple, limit) => {
        if (multiple) {
            if (!limit || !newValue || newValue.length <= limit) {
                setOrgFormdata({
                    ...orgFormdata,
                    [key]: newValue.map((item) => item[0])
                });
            }
        } else if (!newValue) {
            const {[key]: _, ...tempForm} = orgFormdata;
            setOrgFormdata(tempForm);
        } else {
            setOrgFormdata({
                ...orgFormdata,
                [key]: newValue[0]
            });
        }
    }

    const handleNew = (value, key, multiple, limit) => {
        if (!multiple) {
            setOrgFormdata({
                ...orgFormdata,
                [key]: value
            });
        } else {
            if (orgFormdata[key]) {
                if (orgFormdata[key].length < limit) {
                    setOrgFormdata({
                        ...orgFormdata,
                        [key]: [...orgFormdata[key], value]
                    });
                }
            } else {
                setOrgFormdata({
                    ...orgFormdata,
                    [key]: [value]
                });
            }
        }
    }

    const handleSubmitOrg = async () => {
        setLoading(true);
        await postOrg({body: orgFormdata});
        setLoading(false);
    }

    useLayoutEffect(() => {
        if (!(permissions.create || webAdmin)) {
            navigate(from);
        }
    }, []);

    useEffect(() => {
        if (orgPostStatus >= 200 && orgPostStatus < 300) {
            setAlert(false);
            navigate(from);
        } else if (orgPostStatus > 300) {
            setAlert(true);
            setErrorFields(isDict(orgPostResp) ? Object.keys(orgPostResp) : []);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }, [orgPostStatus, orgPostResp]);

    const onBlur = (e) => {
        setTouched({...touched, [e.target.name]: true});
    }

    if (loading) return <LoadingComponent/>

    return (
        <Grid container spacing={2} alignItems="baseline" justifyContent="flex-start"
              sx={{margin: 'auto', mt: 5, width: '80%'}}>
            <Grid size={12}>
                <ErrorDisplay
                    alert={alert}
                    setAlert={setAlert}
                    response={orgPostResp}
                    fields={orgFields}
                    statusCode={orgPostStatus}
                    account
                />
            </Grid>
            <Grid size={12}>
                <NavigateBreadcrumbs alias='organization' display='Organization' create/>
            </Grid>
            <Grid size={12} sx={{mt: 5}}>
                <Typography variant='h4'>New Organization</Typography>
            </Grid>
            {fields.map((field) => (
                <Grid size={{xs: 12, md: 6, xl: 3}} key={`${field.alias}-field`}
                      sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}>
                    <InputField
                        field={field}
                        error={errorFields.includes(field.alias)}
                        onChange={handleOrgChange}
                        onDateChange={handleDateChange}
                        onEnumChange={handleEnumChange}
                        onBlur={onBlur}
                        value={orgFormdata[field.alias]}
                        regexCheck={fieldChecks}
                    />
                </Grid>
            ))}
            <Grid size={12}>
                <RelationView
                    alias='organization'
                    fields={orgFields.relations}
                    handleChange={handleRelationChange}
                    handleNew={handleNew}
                    currentValue={orgFormdata}
                />
            </Grid>
            <Grid size={12} sx={{mt: 10}}>
                <Button variant="contained" onClick={handleSubmitOrg} disabled={error}>Save</Button>
            </Grid>
        </Grid>
    )

}